import { Box, Icon, Theme } from "@outschool/backpack";
import { ReviewableSection } from "@outschool/gql-backend-generated";
import { ReviewableSectionFragment } from "@outschool/gql-frontend-generated";
import { faBan, faCheckCircle, faShare, fasStar } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { addParamsToUrl } from "@outschool/routes";
import { gql, useQueryWithPreviousData } from "@outschool/ui-apollo";
import { TrackedButton } from "@outschool/ui-components-shared";
import { useNavigation } from "@outschool/ui-utils";
import React, { useEffect } from "react";

const REVIEWABLE_SECTION = gql`
  fragment ReviewableSection on ReviewableSection {
    activity_uid
    section_uid
    feedback_uid
    can_review
  }
`;

const REVIEWS_FOR_CLASSES = gql`
  query SectionsForReview {
    currentUser {
      enrolledSectionsThatNeedReview {
        ...ReviewableSection
      }
    }
  }
  ${REVIEWABLE_SECTION}
`;

function prioritizeEntries(arr: $TSFixMe) {
  // Create a Map to store unique entries based on activity_uid
  const resultMap = new Map();

  // Iterate through the array of objects
  arr.forEach((obj: $TSFixMe) => {
    const { activity_uid, can_review, feedback_uid } = obj;

    // Check if the entry for this activity_uid already exists in the Map
    if (resultMap.has(activity_uid)) {
      // Retrieve the existing entry
      const existingEntry = resultMap.get(activity_uid);

      // Determine which entry to prioritize based on the rules
      if (!existingEntry.can_review && can_review) {
        // Replace if current entry can_review is true and existing is false
        resultMap.set(activity_uid, obj);
      } else if (existingEntry.can_review === can_review) {
        // If can_review is the same, prioritize based on feedback_uid
        if (!existingEntry.feedback_uid && feedback_uid) {
          // Replace if current entry feedback_uid is true and existing is false
          resultMap.set(activity_uid, obj);
        }
      }
    } else {
      // If activity_uid is not yet in the Map, add the current object
      resultMap.set(activity_uid, obj);
    }
  });
  return resultMap;
}

export function useActivityReviewEligibility({
  isCurrentUsersProfile
}: {
  isCurrentUsersProfile: boolean;
}) {
  const { data: reviewData } = useQueryWithPreviousData(REVIEWS_FOR_CLASSES, {
    variables: { skip: !isCurrentUsersProfile }
  });
  const [reviewMap, setReviewMap] = React.useState<
    Map<string, ReviewableSectionFragment>
  >(new Map());

  useEffect(() => {
    if (reviewData?.currentUser?.enrolledSectionsThatNeedReview.length) {
      setReviewMap(
        prioritizeEntries(reviewData.currentUser.enrolledSectionsThatNeedReview)
      );
    }
  }, [reviewData]);

  return reviewMap;
}

export function ListingSectionOverride({
  shareOnClick,
  listing,
  reviewItem
}: $TSFixMe) {
  const { t } = useTranslation("client\\routes\\ParentProfile");
  return (
    <Box
      flex
      sx={(theme: Theme) => ({
        flex: "1",
        width: "100%",
        padding: 10,
        flexDirection: "column",
        marginTop: "auto",
        marginBottom: "auto",
        [theme.breakpoints.down("sm")]: {
          flexDirection: "row-reverse",
          gap: "1rem"
        }
      })}
    >
      <TrackedButton
        trackingName="activity listing share variant mobile button"
        trackingUniqueId={listing.activity?.uid}
        variant="contained"
        sx={(theme: Theme) => ({
          display: "flex",
          width: "100%",
          marginBottom: 10,
          [theme.breakpoints.down("sm")]: {
            width: "49%",
            marginBottom: 20
          }
        })}
        onClick={shareOnClick}
      >
        <Icon
          icon={faShare}
          sx={{
            marginRight: "1rem"
          }}
        />
        <Box
          sx={{
            "@media (max-width: 1140px)": {
              display: "none"
            }
          }}
        >
          {t("Share with friend")}
        </Box>
        <Box
          sx={{
            "@media (min-width: 1141px)": {
              display: "none"
            }
          }}
        >
          {t("Share")}
        </Box>
      </TrackedButton>
      {reviewItem && <ReviewButton reviewItem={reviewItem} />}
    </Box>
  );
}

const REVIEW_STATE = {
  REVIEWED: "REVIEWED",
  NO_REVIEW: "NO_REVIEW",
  REVIEWED_LOCKED: "REVIEWED_LOCKED",
  NO_REVIEW_LOCKED: "NO_REVIEW_LOCKED"
};
function getReviewState(reviewItem: $TSFixMe) {
  const { can_review, feedback_uid } = reviewItem;

  if (can_review) {
    if (feedback_uid) {
      return REVIEW_STATE.REVIEWED;
    } else {
      return REVIEW_STATE.NO_REVIEW;
    }
  } else {
    if (feedback_uid) {
      return REVIEW_STATE.REVIEWED_LOCKED;
    } else {
      return REVIEW_STATE.NO_REVIEW_LOCKED;
    }
  }
}

function ReviewButton({ reviewItem }: { reviewItem: ReviewableSection }) {
  const { t } = useTranslation("client\\routes\\ParentProfile");
  const navigate = useNavigation();
  const to = `/classes/${reviewItem?.activity_uid}/feedback`;

  const reviewState = getReviewState(reviewItem);

  const disabled = !reviewItem || reviewState.includes("LOCKED");
  return (
    <TrackedButton
      trackingName={
        reviewState === REVIEW_STATE.NO_REVIEW ||
        reviewState === REVIEW_STATE.REVIEWED
          ? "activity listing edit review variant mobile button"
          : "activity listing review variant mobile button"
      }
      trackingUniqueId={reviewItem?.activity_uid}
      variant="outlined"
      disabled={disabled}
      sx={(theme: Theme) => ({
        display: "flex",
        width: "100%",
        marginTop: 10,
        [theme.breakpoints.down("sm")]: {
          width: "49%",
          marginTop: 0,
          marginBottom: 20
        }
      })}
      onClick={() => {
        navigate(
          addParamsToUrl(to, {
            sectionUid: reviewItem?.section_uid
          }),
          { newTab: true, hardNav: true }
        );
      }}
    >
      {reviewState === REVIEW_STATE.REVIEWED && (
        <>
          <Icon
            icon={faCheckCircle}
            sx={{
              color: "green",
              marginRight: "1rem"
            }}
          />
          <Box
            sx={{
              "@media (max-width: 1140px)": {
                display: "none"
              }
            }}
          >
            {t("Edit review")}
          </Box>
          <Box
            sx={{
              "@media (min-width: 1141px)": {
                display: "none"
              }
            }}
          >
            {t("Reviewed")}
          </Box>
        </>
      )}
      {reviewState === REVIEW_STATE.REVIEWED_LOCKED && (
        <>
          <Icon
            icon={faCheckCircle}
            sx={{
              color: "green",
              marginRight: "1rem"
            }}
          />
          {t("Reviewed")}
        </>
      )}
      {reviewState === REVIEW_STATE.NO_REVIEW && (
        <>
          <Icon icon={fasStar} sx={{ color: "#FFC619", marginRight: "1rem" }} />
          <Box
            sx={{
              "@media (max-width: 1140px)": {
                display: "none"
              }
            }}
          >
            {t("Leave a review")}
          </Box>
          <Box
            sx={{
              "@media (min-width: 1141px)": {
                display: "none"
              }
            }}
          >
            {t("Review")}
          </Box>
        </>
      )}
      {reviewState === REVIEW_STATE.NO_REVIEW_LOCKED && (
        <>
          <Icon
            icon={faBan}
            sx={{ color: "common.grey[700]", marginRight: "1rem" }}
          />
          {t("No Review")}
        </>
      )}
    </TrackedButton>
  );
}
